import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from '~/utils/navigation';

import { insertDepositPixel } from '@comeon/analytics';

import { selectCurrency, selectLocale } from '~/components/account/ducks';
import { gettoolbarLeftMargin } from '~/components/casino-shared/game-loader/ducks/toolbar-utils';
import { hideNotification, showNotification } from '~/components/notifications/ducks/notifications';
import constants from '~/components/payment/common/lib/utils/constants';
import { getPaymentResultUrl } from '~/components/payment/deposit-beta/utils';
import { hydratePaymentMethods } from '~/components/payment/ducks/payment';
import { selectDepositType } from '~/components/payment/ducks/quick-deposit-duck';
import useResultPagePoll from '~/components/payment/hooks/useResultPagePoll';
import {
	getPlayerDetails,
	pendingResultTrackEventCB,
	trackingEventsDepositCallback
} from '~/components/payment/payment-result/utils';
import {
	QD_REDIRECT_URL,
	textKeysResultView as textKeys
} from '~/components/payment/quick-deposit-beta/utils/constants';
import { DepositTypes } from '~/components/payment/types';
import useTranslations from '~/components/translation/use-translations';
import { useCloseCurrentContext } from '~/hooks/use-close-context';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import currencyFormatter from '~/libs/currency-formatter';
import useDepositLimitCheck from '../../../account/limits/deposit/use-deposit-limit-check';

const QdTypeMapping = {
	1: DepositTypes.QUICK_DEPOSIT,
	4: DepositTypes.QUICK_DEPOSIT_SB,
	5: DepositTypes.QUICK_DEPOSIT_GLOBAL
};

const ResultNotification = ({ resultParams = {}, isLandscape, fullLocation }) => {
	const router = useRouter();

	const dispatch = useDispatch();
	const locale = useSelector(selectLocale);
	const currency = useSelector(selectCurrency);
	const quickDepositType = useSelector(selectDepositType);
	const { translations } = useTranslations(textKeys);
	const closeCurrentContext = useCloseCurrentContext();
	const transformPath = useContextualPathTransformer();
	const { handleDepositLimitNotification } = useDepositLimitCheck();

	const { paymentState, pollResult } = useResultPagePoll({
		...resultParams,
		paymentType: 'deposit'
	});

	const closeNotificationCB = () => {
		dispatch(hideNotification());
		dispatch(hydratePaymentMethods());
		window.localStorage.setItem(QD_REDIRECT_URL, JSON.stringify(fullLocation));
		router.push(transformPath(getPaymentResultUrl(resultParams)));
	};

	const showIncompleteNotification = (message, color) =>
		dispatch(
			showNotification({
				message: message || pollResult?.errorMessage.title || translations.QUICK_DEPOSIT_RESULT_FAIL_TITLE,
				color: color || 'red',
				buttonText: translations.QD_LEARN_MORE_BUTTON_TEXT,
				buttonInline: true,
				buttonCB: closeNotificationCB,
				onClose: closeCurrentContext,
				onCloseClickCallback: closeCurrentContext,
				customMargin: gettoolbarLeftMargin(isLandscape)
			})
		);

	const showSuccessNotification = message =>
		dispatch(
			showNotification({
				message,
				color: 'green',
				autoHide: true,
				customMargin: gettoolbarLeftMargin(isLandscape)
			})
		);

	const viewUpdateCallback = (data, state) => {
		if (!data) {
			return;
		}

		if (state === constants.SUCCESS || state === constants.PENDING) {
			handleDepositLimitNotification(data?.accHistRef);
		}

		const depositType = QdTypeMapping[quickDepositType];
		const playerData = getPlayerDetails(pollResult);

		if (state === constants.SUCCESS) {
			insertDepositPixel(playerData, trackingEventsDepositCallback(true, null, depositType));
			showSuccessNotification(
				`${currencyFormatter.format(pollResult.amount, currency, locale)} ${translations.QUICK_DEPOSIT_SUCCESS}`
			);
		} else if (state === constants.PENDING) {
			const pendingColor = 'neutral';
			showIncompleteNotification(translations.QUICK_DEPOSIT_PENDING, pendingColor);

			if (!playerData?.is_empty) {
				insertDepositPixel(playerData, trackingEventsDepositCallback(true, null, depositType));
			} else {
				pendingResultTrackEventCB(pollResult, depositType);
			}
		} else {
			const trackEventCB = trackingEventsDepositCallback(false, data?.errorMessage, depositType);
			trackEventCB(data?.pixelTrackingData);

			showIncompleteNotification();
		}

		closeCurrentContext();
	};

	useEffect(() => {
		if (pollResult?.initialAttemptedAmount) {
			showSuccessNotification(
				`${currencyFormatter.format(pollResult?.initialAttemptedAmount, currency, locale)} ${
					translations.QUICK_DEPOSIT_PARTIAL_SUCCESS_P1
				} ${currencyFormatter.format(pollResult?.amount, currency, locale)} ${
					translations.QUICK_DEPOSIT_PARTIAL_SUCCESS_P2
				} `
			);
			closeCurrentContext();
		}

		if (paymentState) {
			viewUpdateCallback(pollResult, paymentState);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paymentState, pollResult]);

	return null;
};

export default ResultNotification;
